import type { RestRecord } from '@libs/data-access/entities'
import {
  Tooltip,
  IconWrap,
  PencilIcon,
  InformationCircleBoldIcon,
  Flex,
  Button,
  Text,
  Loader
} from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import {
  Step,
  updateStep
} from '@spa/scenes/Plannings/Modals/RestModal/redux/modules/main'
import type { FC } from 'react'
import { useDispatch } from 'react-redux'

import { RestRevaluatedBy } from '../RestRevaluatedBy/RestRevaluatedBy'

const DEFAULT = 'default'
const CUSTOM = 'custom'

type Props = {
  rest: RestRecord
  canSetCustomRestValue: boolean
  defaultRestDuration: number
  formLoading: boolean
  restDurationOption: string
  revaluatedRestDuration: number
  onResetDefaultRestDuration: Function
  restCantBeEdited: boolean
}
export const DefaultRestDurationOrRevaluation: FC<Props> = ({
  canSetCustomRestValue,
  onResetDefaultRestDuration,
  defaultRestDuration,
  formLoading,
  restDurationOption,
  revaluatedRestDuration,
  restCantBeEdited,
  rest
}) => {
  const { t, tDates } = usePlanningFormTranslation()
  const dispatch = useDispatch()

  const handleOpenRavaluationTable = () => {
    dispatch(updateStep(Step.REVALUATION_FORM))
  }

  const isDefault = restDurationOption === DEFAULT
  const isCustom = restDurationOption === CUSTOM
  const isDisabled = formLoading || !canSetCustomRestValue
  const customRestModifier = rest.rest_value_updated_by
  const customRestModifiedAt = rest.rest_value_updated_at

  const restDuration = isDefault ? defaultRestDuration : revaluatedRestDuration

  return (
    <>
      <Flex row className='flex-nowrap justify-between'>
        <Flex row className='items-center justify-center gap-1'>
          <Text className='text-s'>
            {t('restForm.defaultRestDurationTitle')}
          </Text>
          <Tooltip
            id='revalo-tooltip-info'
            label={
              canSetCustomRestValue
                ? t('restForm.snapshiftCustomDuration')
                : t('restForm.noPrivileges')
            }
            position='bottom'
          >
            <IconWrap icon={<InformationCircleBoldIcon />} />
          </Tooltip>
        </Flex>
        <div>
          {formLoading && <Loader />}
          {!formLoading && (
            <Tooltip
              id='revalo-tooltip-edit'
              label={t('restForm.editDuration')}
              position='left'
            >
              <Button
                onClick={handleOpenRavaluationTable}
                disabled={
                  isDisabled || !canSetCustomRestValue || restCantBeEdited
                }
                variant='tertiary'
                intent='neutral-dark'
                rightIcon={<PencilIcon />}
              >
                {tDates('duration.hourDecimalToHours', {
                  duration: restDuration
                })}
              </Button>
            </Tooltip>
          )}
        </div>
      </Flex>
      <div className='grid grid-cols-2'>
        {customRestModifier && (
          <RestRevaluatedBy
            customRestModifier={customRestModifier}
            customRestModifiedAt={customRestModifiedAt}
          />
        )}
        {isCustom && !formLoading && restDuration !== defaultRestDuration && (
          <div
            className={`${customRestModifier ? 'col-span-1' : 'col-span-2'} justify-self-end`}
          >
            <Button
              onClick={() => onResetDefaultRestDuration()}
              disabled={isDisabled || !canSetCustomRestValue}
              variant='secondary'
              rightIcon={<PencilIcon />}
            >
              {t('restForm.resetTo', {
                value: defaultRestDuration * 60
              })}
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
